import React, { useState, useEffect, useCallback } from 'react';
import { X, RefreshCw, AlertCircle } from 'lucide-react';
import syncService, { SPR_DVM_PREFIX } from '../services/syncService';
import Button from './shared/Button';
import Spinner from './shared/Spinner';

// Data Value Mapping tool (superadmin). Opened from a message-log story line whose error indicates a
// missing DVM (e.g. CRM11012 Invalid Preference Type Combination). Reads the target OHIP's conversion
// codes + current mapped values and shows the operator exactly what to create — all through the
// existing ohip-simulate proxy (target-connector credentials, no extra backend).
// DVM API: /dvm/v1 (github.com/oracle/hospitality-api-docs rest-api-specs/property/v1/dvm.json)
//   GET  /externalSystems/{ext}/conversionCodes?configuredOnly=            (list codes, UPPER_SNAKE)
//   GET  /externalSystems/{ext}/hotels/{hotelId}/convertedValues?conversionCode=   (actual values)
// The values endpoint takes conversionCode as the dataValueMappingCodeType enum in PascalCase
// (GuestPreferenceCode), NOT the UPPER_SNAKE code the list returns — convert with toPascalCode().
// It REQUIRES the x-externalsystem header (sim() sets it); response is a `convertedValues` array of
// { operaValue, externalValue, masterValue, conversionCode, pmsDefaultConversion, crsDefaultConversion }.
//
// seed: { externalSystem, hotelId, value, typeCode, line }
function DvmModal({ seed, syncId, taskId, recordUuid, onClose }) {
  const [externalSystem, setExternalSystem] = useState(seed.externalSystem || '');
  const [hotelId, setHotelId] = useState(seed.hotelId || '');
  // The simulate proxy is WYSIWYG — it only sends the headers we give it (plus the bearer token).
  // OHIP requires x-app-key on every call, so bootstrap it (and the hotel) from the proxy's
  // tokenOnly mode, which returns the target connector's appKey/hotelId.
  const [appKey, setAppKey] = useState(seed.appKey || '');
  // DVM is enterprise-scoped (the target sets operaEntId=<enterprise> on responses) — creation
  // silently fails without the enterprise context header.
  const [enterpriseId, setEnterpriseId] = useState(seed.enterpriseId || '');
  const [codes, setCodes] = useState([]);           // available conversion codes
  const [code, setCode] = useState('');             // selected conversion code
  const [codesLoading, setCodesLoading] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);
  // Empty by default so the full value list loads (the failing seed.value still sorts to the top).
  // When set (typed, or via a combination-hint button) it filters convertedValues server-side.
  const [searchText, setSearchText] = useState('');
  const [error, setError] = useState(null);

  // Create-mapping form state.
  const [newOpera, setNewOpera] = useState('');
  const [newExternal, setNewExternal] = useState(seed.value || '');
  const [newGlobal, setNewGlobal] = useState(false);
  const [newDefault, setNewDefault] = useState(true);   // both direction defaults, like existing rows
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState(null);      // { ok, text }
  // Combination create (CRM11012 [type/value]) — per-code OPERA value + status, keyed by conversion code.
  const [comboOpera, setComboOpera] = useState({});      // { [code]: operaValue }
  const [comboStatus, setComboStatus] = useState({});    // { [code]: { creating, ok, text } }

  // Mapping CREATION uses postDataValueMappings — the SAME endpoint that previously appeared "blocked".
  // Two things were going on: (1) the missing x-externalsystem header (sim() now always sends it), and
  // (2) OHIP always echoes a benign { reason: "Initialization failed" } in conversionCodesFailed even
  // on a successful create — createMapping ignores that reason. conversionCode here is the UPPER_SNAKE
  // `code` (path param), NOT the PascalCase enum used by convertedValues. Any OTHER failed reason still
  // surfaces the console-fallback note below.
  //   POST /externalSystems/{ext}/conversionCodes/{code}/dataValueMappings
  //        { conversionCodeMappings: [{ operaValue, externalValue, hotelId?, isGlobal,
  //                                      operaToExternalDefault, externalToOperaDefault }] }

  const sim = (method, url, body) => syncService.ohipSimulate(syncId, taskId, {
    method, url,
    headers: {
      ...(appKey ? { 'x-app-key': appKey } : {}),
      ...(hotelId ? { 'x-hotelid': hotelId } : {}),
      ...(enterpriseId ? { 'x-enterpriseid': enterpriseId } : {}),
      // convertedValues resolves the external system from this header — REQUIRED, or it 400s.
      ...(externalSystem ? { 'x-externalsystem': externalSystem } : {}),
    },
    ...(body ? { body } : {}),
  });

  // The values endpoint's conversionCode is the PascalCase dataValueMappingCodeType enum, but the
  // list endpoint (and our dropdown) uses UPPER_SNAKE — e.g. GUEST_PREFERENCE_CODE -> GuestPreferenceCode.
  const toPascalCode = (c) => String(c || '')
    .split(/[_\s]+/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');

  // Bootstrap connector context (appKey / hotelId / externalSystem / enterpriseId) once. tokenOnly
  // returns all four from the target connector — fall back to them when the seed (message-log headers)
  // didn't carry them, so x-externalsystem is always available for the convertedValues call.
  useEffect(() => {
    (async () => {
      try {
        const t = await syncService.ohipSimulate(syncId, taskId, { tokenOnly: true });
        if (t && t.appKey && !appKey) setAppKey(t.appKey);
        if (t && t.hotelId && !hotelId) setHotelId(t.hotelId);
        if (t && t.externalSystem && !externalSystem) setExternalSystem(t.externalSystem);
        if (t && t.enterpriseId && !enterpriseId) setEnterpriseId(t.enterpriseId);
      } catch (e) {
        setError(e.response?.data?.message || e.message || 'Failed to load connector credentials');
      }
    })();
  }, []); // eslint-disable-line

  // Pull an array out of the DVM response defensively — Oracle wraps lists differently per endpoint.
  const firstArray = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    for (const v of Object.values(obj)) {
      if (Array.isArray(v)) return v;
      if (v && typeof v === 'object') { const a = firstArray(v); if (a.length) return a; }
    }
    return [];
  };

  const loadCodes = useCallback(async () => {
    if (!externalSystem || !appKey) return;
    setCodesLoading(true); setError(null);
    try {
      const res = await sim('GET', `/dvm/v1/externalSystems/${encodeURIComponent(externalSystem)}/conversionCodes${hotelId ? `?hotelId=${encodeURIComponent(hotelId)}` : ''}`);
      if (res && res.status >= 400) throw new Error(`GET conversionCodes → ${res.status}`);
      const body = res && res.response;
      const raw = (body && Array.isArray(body.conversionCodes)) ? body.conversionCodes : firstArray(body);
      // Dedupe (the target returns some codes twice) and keep label/group for display.
      const seen = new Set();
      const list = [];
      for (const c of raw) {
        const codeVal = (c && (c.conversionCode || c.code || c.name)) || null;
        if (!codeVal || seen.has(codeVal)) continue;
        seen.add(codeVal);
        list.push({ code: codeVal, label: c.label || codeVal, group: c.group || '' });
      }
      list.sort((a, b) => a.label.localeCompare(b.label));
      setCodes(list);
      // Preselect the best match for the seed (preference errors → GUEST_PREFERENCE_CODE).
      if (!code && list.length) {
        const guess = seed.codeHint ? list.find((c) => c.code.toUpperCase().includes(seed.codeHint)) : null;
        setCode((guess || list[0]).code);
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load conversion codes');
    } finally {
      setCodesLoading(false);
    }
  }, [externalSystem, hotelId, appKey]); // eslint-disable-line

  const loadMappings = useCallback(async () => {
    if (!externalSystem || !code || !appKey || !hotelId) return;
    setMappingsLoading(true); setError(null);
    try {
      const qs = [
        `conversionCode=${encodeURIComponent(toPascalCode(code))}`,
        searchText.trim() ? `valueToBeConverted=${encodeURIComponent(searchText.trim())}` : null,
      ].filter(Boolean).join('&');
      const res = await sim('GET', `/dvm/v1/externalSystems/${encodeURIComponent(externalSystem)}/hotels/${encodeURIComponent(hotelId)}/convertedValues?${qs}`);
      // 204 = no converted values for this code/value (a valid "nothing mapped" answer, not an error).
      if (res && res.status === 204) { setMappings([]); return; }
      if (res && res.status >= 400) throw new Error(`GET convertedValues → ${res.status}`);
      // Spec (convertedValuesType): the list is `convertedValues`; fall back to any array defensively.
      const body = res && res.response;
      setMappings((body && Array.isArray(body.convertedValues)) ? body.convertedValues : firstArray(body));
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load mappings');
      setMappings([]);
    } finally {
      setMappingsLoading(false);
    }
  }, [externalSystem, hotelId, code, appKey, searchText]); // eslint-disable-line

  // POST one mapping to a specific conversion code. Returns { ok, text }; throws on transport error.
  const postMapping = async (conversionCode, operaValue, externalValue) => {
    const mapping = {
      operaValue: operaValue.trim(),
      externalValue: externalValue.trim(),
      isGlobal: newGlobal,
      operaToExternalDefault: newDefault,
      externalToOperaDefault: newDefault,
    };
    // Property-scoped rows carry the hotelId; global rows must not.
    if (!newGlobal && hotelId) mapping.hotelId = hotelId;
    const res = await sim(
      'POST',
      `/dvm/v1/externalSystems/${encodeURIComponent(externalSystem)}/conversionCodes/${encodeURIComponent(conversionCode)}/dataValueMappings`,
      { conversionCodeMappings: [mapping] },
    );
    if (res && res.status >= 400) throw new Error(`POST dataValueMappings → ${res.status}`);
    // OHIP returns 201 and ALWAYS echoes a benign { reason: "Initialization failed" } item in
    // conversionCodesFailed even on a successful create (confirmed on HCONN1/OHIP-HCONNECT) — ignore
    // that reason and treat only OTHER reasons as a genuine failure.
    const body = res && res.response;
    const failedRaw = (body && (body.conversionCodesFailed || body.conversionCodeMappingsFailed || body.failed)) || [];
    const realFailed = (Array.isArray(failedRaw) ? failedRaw : [])
      .filter((f) => !/initialization failed/i.test((f && f.reason) || ''));
    if (realFailed.length) return { ok: false, text: `OHIP rejected it. ${JSON.stringify(realFailed).slice(0, 200)}` };
    // Record the successful create on the SPR record's status history (surfaced in the Message Log
    // history tab + the reconciliation report). Best-effort: the mapping is already created.
    if (recordUuid) {
      try {
        await syncService.addSprComment(syncId, taskId, recordUuid,
          `${SPR_DVM_PREFIX} mapping created: ${conversionCode} ${externalValue.trim()} → ${operaValue.trim()}`);
      } catch (e) { /* non-fatal */ }
    }
    return { ok: true, text: `Created ${operaValue.trim()} → ${externalValue.trim()}` };
  };

  // Single-row create (non-combination): uses the selected dropdown code.
  const createMapping = async () => {
    if (!externalSystem || !code || !newOpera.trim() || !newExternal.trim()) return;
    setCreating(true); setCreateMsg(null); setError(null);
    try {
      const r = await postMapping(code, newOpera, newExternal);
      setCreateMsg(r);
      if (r.ok) { setNewOpera(''); await loadMappings(); }
    } catch (e) {
      setCreateMsg({ ok: false, text: e.response?.data?.message || e.message || 'Create failed' });
    } finally {
      setCreating(false);
    }
  };

  // Combination create: one line per code (type → GUEST_PREFERENCE_TYPE, value → GUEST_PREFERENCE_CODE).
  const createCombo = async (row) => {
    const ov = (comboOpera[row.code] || '').trim();
    if (!externalSystem || !ov || !row.external) return;
    setComboStatus((s) => ({ ...s, [row.code]: { creating: true } }));
    try {
      const r = await postMapping(row.code, ov, row.external);
      setComboStatus((s) => ({ ...s, [row.code]: r }));
      if (r.ok) await loadMappings();
    } catch (e) {
      setComboStatus((s) => ({ ...s, [row.code]: { ok: false, text: e.response?.data?.message || e.message || 'Create failed' } }));
    }
  };

  useEffect(() => { loadCodes(); }, [loadCodes]);
  useEffect(() => { loadMappings(); }, [loadMappings]);

  // Never hide rows client-side — sort hits for the failing value to the top instead.
  const hit = (m) => seed.value && `${m.operaValue || ''} ${m.externalValue || ''}`.toUpperCase().includes(String(seed.value).toUpperCase());
  const shown = [...mappings].sort((a, b) => (hit(b) ? 1 : 0) - (hit(a) ? 1 : 0));

  // A CRM11012 combination [type/value] needs a mapping on BOTH preference codes — the type under
  // GUEST_PREFERENCE_TYPE and the value under GUEST_PREFERENCE_CODE — so offer one create line each.
  const comboRows = (seed.typeCode && seed.value) ? [
    { code: 'GUEST_PREFERENCE_TYPE', title: 'Preference Type', external: seed.typeCode },
    { code: 'GUEST_PREFERENCE_CODE', title: 'Preference',      external: seed.value },
  ] : null;

  return (
    <div className="modal-overlay ohip-sim-overlay" onClick={onClose}>
      <div className="modal-content ohip-sim-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Data Value Mappings</h2>
          <button className="modal-close" onClick={onClose}><X className="icon-sm" /></button>
        </div>
        <div className="modal-body">
          <p className="form-hint" style={{ marginTop: 0 }}>
            Read from the task's <strong>target OHIP</strong> via the simulate proxy. Triggered by:&nbsp;
            <code className="dvm-trigger-line">{seed.line}</code>
          </p>

          <div className="dvm-controls">
            <div className="form-group">
              <label className="form-label">External system</label>
              <input className="form-input" value={externalSystem} onChange={(e) => setExternalSystem(e.target.value)} placeholder="e.g. OHIP-HCONNECT" />
            </div>
            <div className="form-group">
              <label className="form-label">Hotel</label>
              <input className="form-input" value={hotelId} onChange={(e) => setHotelId(e.target.value)} placeholder="e.g. HCONN1" />
            </div>
            <div className="form-group">
              <label className="form-label">Enterprise</label>
              <input className="form-input" value={enterpriseId} onChange={(e) => setEnterpriseId(e.target.value)} placeholder="e.g. EHCON" />
            </div>
            <div className="form-group">
              <label className="form-label">Conversion code</label>
              <select className="form-input" value={code} onChange={(e) => setCode(e.target.value)}>
                {!codes.length && <option value="">{codesLoading ? 'Loading…' : '— load codes —'}</option>}
                {codes.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
              </select>
            </div>
            <Button variant="secondary" onClick={() => { loadCodes(); loadMappings(); }} disabled={codesLoading || mappingsLoading}>
              <RefreshCw className="icon-sm" /> {codesLoading || mappingsLoading ? 'Loading…' : 'Refresh'}
            </Button>
          </div>

          {error && <div className="error-banner"><AlertCircle className="icon-sm" />{error}</div>}

          {/* A preference COMBINATION [type/value] can need mappings on BOTH codes — quick views. */}
          {seed.typeCode && seed.value && (
            <div className="dvm-combo-hint">
              Combination <strong>[{seed.typeCode}/{seed.value}]</strong> — check both:
              <button type="button" className="ohip-sim-addheader" onClick={() => { setCode('GUEST_PREFERENCE_CODE'); setSearchText(seed.value); }}>
                value {seed.value} (GUEST_PREFERENCE_CODE)
              </button>
              <button type="button" className="ohip-sim-addheader" onClick={() => { setCode('GUEST_PREFERENCE_TYPE'); setSearchText(seed.typeCode); }}>
                type {seed.typeCode} (GUEST_PREFERENCE_TYPE)
              </button>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Current mappings {code ? `— ${code}` : ''} {seed.value ? `(matching "${seed.value}" first)` : ''}</label>
            {mappingsLoading ? <div className="message-log-loading"><Spinner /></div> : (
              <div className="dvm-table-wrap">
                <table className="dvm-table">
                  <thead><tr><th>OPERA value</th><th>External value</th><th>Master</th><th>PMS default</th><th>CRS default</th></tr></thead>
                  <tbody>
                    {shown.length === 0 && <tr><td colSpan={5} className="dvm-empty">No converted values returned{seed.value ? ` for "${seed.value}"` : ''}.</td></tr>}
                    {shown.slice(0, 100).map((m, i) => (
                      <tr key={i} className={seed.value && `${m.externalValue || ''}`.toUpperCase() === String(seed.value).toUpperCase() ? 'dvm-row-hit' : ''}>
                        <td>{m.operaValue || '—'}</td>
                        <td>{m.externalValue || '—'}</td>
                        <td>{m.masterValue || '—'}</td>
                        <td>{m.pmsDefaultConversion ? 'Y' : 'N'}</td>
                        <td>{m.crsDefaultConversion ? 'Y' : 'N'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="dvm-add sa-only-panel">
            {comboRows ? (
              <>
                <label className="form-label">Create the missing mappings — combination [{seed.typeCode}/{seed.value}]</label>
                <p className="dvm-console-note" style={{ marginTop: 0 }}>
                  This combination needs a mapping on <strong>both</strong> preference codes. Enter the
                  existing OPERA code at <strong>{hotelId || 'the property'}</strong> for each, then create.
                </p>
                {comboRows.map((row) => {
                  const st = comboStatus[row.code] || {};
                  const ov = comboOpera[row.code] || '';
                  return (
                    <div key={row.code} className="dvm-add-row">
                      <div className="form-group">
                        <label className="form-label">OPERA value — {row.title} <span className="dvm-code-tag">{row.code}</span></label>
                        <input className="form-input" value={ov} placeholder="existing code at the property"
                          onChange={(e) => setComboOpera((s) => ({ ...s, [row.code]: e.target.value }))} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">External value</label>
                        <input className="form-input" value={row.external} readOnly />
                      </div>
                      <Button variant="primary" onClick={() => createCombo(row)} disabled={st.creating || st.ok || !ov.trim()}>
                        {st.creating ? 'Creating…' : st.ok ? 'Created ✓' : 'Create'}
                      </Button>
                      {st.text && (
                        <span className={st.ok ? 'dvm-create-ok' : 'error-banner'} style={{ flexBasis: '100%' }}>
                          {!st.ok && <AlertCircle className="icon-sm" />}{st.text}
                        </span>
                      )}
                    </div>
                  );
                })}
                <div className="dvm-add-row">
                  <label className="dvm-add-check" title="Global (all properties) instead of scoped to this hotel">
                    <input type="checkbox" checked={newGlobal} onChange={(e) => setNewGlobal(e.target.checked)} /> Global
                  </label>
                  <label className="dvm-add-check" title="Set as the default conversion in both directions (like existing rows)">
                    <input type="checkbox" checked={newDefault} onChange={(e) => setNewDefault(e.target.checked)} /> Default both ways
                  </label>
                </div>
              </>
            ) : (
              <>
                <label className="form-label">Add a mapping {code ? `— ${code}` : ''}</label>
                <p className="dvm-console-note" style={{ marginTop: 0 }}>
                  Creates the mapping on the <strong>target OHIP</strong> ({externalSystem || '—'} @ {hotelId || '—'})
                  via the DVM API. OPERA value must be an existing code at the property.
                </p>
                <div className="dvm-add-row">
                  <div className="form-group">
                    <label className="form-label">OPERA value</label>
                    <input className="form-input" value={newOpera} onChange={(e) => setNewOpera(e.target.value)} placeholder="existing code at the property" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">External value</label>
                    <input className="form-input" value={newExternal} onChange={(e) => setNewExternal(e.target.value)} placeholder="e.g. the failing incoming value" />
                  </div>
                  <label className="dvm-add-check" title="Global (all properties) instead of scoped to this hotel">
                    <input type="checkbox" checked={newGlobal} onChange={(e) => setNewGlobal(e.target.checked)} /> Global
                  </label>
                  <label className="dvm-add-check" title="Set as the default conversion in both directions (like existing rows)">
                    <input type="checkbox" checked={newDefault} onChange={(e) => setNewDefault(e.target.checked)} /> Default both ways
                  </label>
                  <Button variant="primary" onClick={createMapping} disabled={creating || !code || !newOpera.trim() || !newExternal.trim()}>
                    {creating ? 'Creating…' : 'Create mapping'}
                  </Button>
                </div>
                {createMsg && (
                  <div className={createMsg.ok ? 'dvm-create-ok' : 'error-banner'}>
                    {!createMsg.ok && <AlertCircle className="icon-sm" />}{createMsg.text}
                  </div>
                )}
              </>
            )}
            <p className="dvm-console-note">
              OPERA caches DVM for <strong>15 minutes</strong> (per the console's own banner) — after a
              successful create, wait before reprocessing, then Refresh here to verify.
            </p>

            <details className="dvm-console-fallback">
              <summary>Can't create via API? Add it in the OPERA console</summary>
              <p className="dvm-console-note">
                In OPERA Cloud go to <strong>Exchange → Data Value Mappings</strong>, Property
                <strong> {hotelId || '—'}</strong>, Interface <strong>{externalSystem || '—'}</strong>,
                then add under the matching screen (both direction ticks on, like the existing rows):
              </p>
              <ul className="dvm-console-list">
                {seed.typeCode && (
                  <li><strong>Guest Preference Type</strong>: OPERA = the property's real type (e.g. NEWSPAPER), External Code = <code>{seed.typeCode}</code> — only if the type isn't already an exact OPERA type name.</li>
                )}
                {seed.value && (
                  <li><strong>Guest Preference</strong>: OPERA = an existing preference code at {hotelId || 'the property'}, External Code = <code>{seed.value}</code>.</li>
                )}
                {!seed.value && <li>OPERA value = an existing code at the property; External Code = the incoming value shown in the failing call.</li>}
              </ul>
            </details>
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} type="button">Close</Button>
        </div>
      </div>
    </div>
  );
}

export default DvmModal;
